import React, { useEffect, useMemo, useState } from 'react'
import { ATBButtonPopover } from '../../add-to-basket-button-popover'
import { AddToFavouritesButton } from '../add-to-favourites-button'
import { Box, Container, Flex, Stack, Text, useDisclosure } from '../../../vanilla'
import { CART_LOCATION } from '../../../analytics/utils'
import { Carousel } from '../../carousel'
import { EVENT_NAMES_MAP } from '../../../analytics/algolia'
import { Hit } from '../../../store/ProductStore'
import { InfoIcon } from '../../icons'
import { LearnMoreAlcoholRestrictionModal } from '../../alcohol-restriction-modal'
import { MultiBuyPromoOfferMessage, NearPromo } from '../product-promo'
import { PDPBadgeAsset } from '../../cms/product-badges'
import { Price } from '../../price'
import { ProductItem } from '../../../store/BasketStore'
import { StarRating } from '../../star-rating'
import { TopRightProductBadgeGroup } from '../../top-right-product-badges/top-right-product-badge-group'
import { detailsContainer, imageContainer, imageThumbnailSpacings } from './styles.css'
import { observer } from 'mobx-react'
import { transformHit } from '../../algolia/util/transformHit'
import { useAddToCartTracking } from '../../../analytics/click-event-tracking'
import { useAlcoholRestrictions } from '../../../hooks/use-alcohol-restrictions'
import { useBasketItem } from '../../../store/hooks/useBasketItem'
import { useBookDeliveryPopover } from '../../../contexts'
import { useFetchBadges } from '../../cms/product-badges/useFetchBadges'
import { useItemPrice } from '../../algolia/util/useItemPrice'
import { useProductDetail } from '../../../store/hooks/useProductDetail'
import { useProductImage } from '../../../store/hooks/useProductImage'
import { useSlotExpiration } from '../../../store/hooks/useSlotExpiration'
import { getPromoExpiryText } from '../../../store/PromotionStore'
import {
  useGlobalStore,
  useBasketStore,
  useOrderStore,
  useCustomerStore
} from '../../../store/hooks/useStore'
import debounce from 'lodash.debounce'
import UpdateCartItemQuantity from '../../update-cart-quantity'
import useCustomSitePreference from '../../../hooks/forms/useCustomSitePreference'
import { ViewAlternativesButton } from '../../view-alternatives-button'
import { isProductPurchaseDisabled } from '../../../utils/utils'
import { ProductOverlay } from '../../product-overlay'

const MIN_LENGTH_TO_SHOW_CAROUSEL_ARROWS = 2

export const ProductBuyModule = observer(
  ({
    productDetail,
    openReviewTabAndScroll,
  }: {
    productDetail: ReturnType<typeof useProductDetail>
    customerGroups: string[]
    openReviewTabAndScroll: () => void
  }) => {
    const product = productDetail.product
    const {
      imageGroups,
      productRating,
      productReviewCount,
      id,
      priceInfo,
      drainedWeight,
      pdpTopRightTileBadge,
      pdpMiddleRightTileBadge,
      pdpBottomRightTileBadge,
      pdpBadgeAsset,
      productPromotions,
      hitItemIndex
      } = product

    const {getApplicablePromotions} = useCustomerStore()
    const {nearPromo, bundle} = getApplicablePromotions(productPromotions)
    const badgeKeys = useMemo(
      () =>
        [
          pdpTopRightTileBadge ?? null,
          pdpMiddleRightTileBadge ?? null,
          pdpBottomRightTileBadge ?? null,
          pdpBadgeAsset ?? null,
        ].filter(Boolean),
      [pdpTopRightTileBadge, pdpMiddleRightTileBadge, pdpBottomRightTileBadge, pdpBadgeAsset],
    )
    const {selectedStoreId, selectedPriceDayOffset, selectedRegion} = useGlobalStore()
    const basketStore = useBasketStore()
    const sendAddToCartData = useAddToCartTracking(basketStore.basket)
    const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()
    const hit = transformHit(product as unknown as Hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) as Hit
    const {quantity} = useBasketItem(hit.id)
    const [clickedQuantity, setClickedQuantity] = useState(0)
    const {editMode} = useOrderStore()
    const learnMoreAlcRestrictionModal = useDisclosure()
    const {checkIfProductAlcohol, isCurrentSlotAlcoholRestricted} = useAlcoholRestrictions()
    const isAlcohol = checkIfProductAlcohol(hit)
    const [currentSelectedImageGroupIndex, setCurrentSelectedImageGroupIndex] = useState(0)
    const {displayPrice, promotionPrice} = useItemPrice(product)
    const {showBookDeliveryPopover} = useBookDeliveryPopover()
    const showCarousel = (imageGroups?.[0]?.images?.length ?? 0) > 1
    const showCarouselArrows =
    (imageGroups?.[0]?.images.length ?? 0) > MIN_LENGTH_TO_SHOW_CAROUSEL_ARROWS

    const isPurchaseDisabled = isProductPurchaseDisabled(!!displayPrice, hit.availableForDayAtStore, hit.isAddToCartDisabled)

    // Restricted Items
    const restrictedProductInfo = basketStore.getRestrictedProductInfo(hit.productClasses, hit.id)
    const disabledProductBecauseOfRestriction = isAlcohol && isCurrentSlotAlcoholRestricted

    useEffect(() => {
      setClickedQuantity(quantity)
    }, [basketStore.count])

    useFetchBadges(badgeKeys)

    const debouncedPrev = useMemo(
      () =>
        debounce(({leftReached, previous}) => {
          if (!leftReached) {
            previous?.()
            setCurrentSelectedImageGroupIndex(currentSelectedImageGroupIndex - 1)
          } else if (currentSelectedImageGroupIndex > 0) {
            setCurrentSelectedImageGroupIndex(0)
          }
        }, 150),
      [currentSelectedImageGroupIndex],
    )

    const debouncedNext = useMemo(
      () =>
        debounce(({rightReached, next}) => {
          if (!rightReached) {
            next?.()
            setCurrentSelectedImageGroupIndex(currentSelectedImageGroupIndex + 1)
          } else if (currentSelectedImageGroupIndex < (imageGroups?.[0]?.images?.length ?? 0) - 1) {
            setCurrentSelectedImageGroupIndex(currentSelectedImageGroupIndex + 1)
          }
        }, 150),
      [currentSelectedImageGroupIndex],
    )

    const addToBasket = (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation()
      e.preventDefault()

      if (editMode && isReservationExpired()) {
        // If in edit mode and slot has expired send to order history and don't add to basket
        handleEditReservationExpired()
      } else if (!basketStore.canAddItems) {
        showBookDeliveryPopover({
          clickedHitId: hit.id,
          bookDeliveryProps: {
            product: hit as ProductItem,
          },
        })
      } else {
        const event = hit.__queryID
          ? EVENT_NAMES_MAP.PDP_ADD_TO_CART_AFTER_SEARCH
          : EVENT_NAMES_MAP.PDP_ADD_TO_CART
        basketStore.addProductToUpdate({product: hit as ProductItem, quantity: 1, event: event})
        setClickedQuantity(1)
      }
    }

    const showReviews = useCustomSitePreference('ShowReviews')

    return (
      <Container bg="white" paddingX={['16px', '0px']}>
        <Flex flexDirection={['column', 'row']} position="relative">
          <Stack
            paddingTop={['36px', '20px']}
            paddingRight={['16px', '0px']}
            spacing="12px"
            display={['flex', 'none']}
          >
            <Text variant="unstyled" fontSize="md" fontWeight="normal">
              {hit?.name}
            </Text>
            {drainedWeight && (
              <Text variant="text1" color="textMuted">
                {drainedWeight}
              </Text>
            )}
            {!!productReviewCount && showReviews && (
              <StarRating
                rating={productRating}
                reviewCount={productReviewCount}
                onReviewClick={openReviewTabAndScroll}
              />
            )}
          </Stack>
          <Box className={imageContainer} position="relative">
            <TopRightProductBadgeGroup
              hit={hit}
              top={[40, 85, 130]}
              right={15}
            />
            {imageGroups?.map(({images}) => {
              const imgSrc = useProductImage(images?.[currentSelectedImageGroupIndex])

              return (
                <Box
                  key={`${imgSrc}-${currentSelectedImageGroupIndex}`}
                  aspectRatio="productImage"
                  marginX="auto"
                  width="full"
                  style={{maxWidth: 270, minWidth: 250, position: 'relative'}}
                  backgroundColor="gray100"
                >
                  <ProductOverlay 
                    isAlcoholRestriction={disabledProductBecauseOfRestriction}
                    isUnavailable={isPurchaseDisabled}
                    isRestricted={restrictedProductInfo?.cannotAddToBasket && hit.productClasses?.length > 0}
                    onAlcoholLearnMoreClick={learnMoreAlcRestrictionModal.onToggle}
                    hit={hit}
                  />
                  {imgSrc &&
                    <img
                      id="productImage"
                      src={`${imgSrc}$pdpmain_2x$`}
                      alt={images?.[currentSelectedImageGroupIndex]?.alt}
                      title={images?.[currentSelectedImageGroupIndex]?.title}
                      loading="eager"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  }
                </Box>
              )
            })}
            {showCarousel && (
              <Box marginX="auto" style={{maxWidth: 108}} position="relative" marginTop="8px">
                <Carousel
                  autoPlay={false}
                  loop={false}
                  itemClassName={imageThumbnailSpacings}
                  controlsPosition="outerCenter"
                  customResponsiveConfig={{
                    desktop: {
                      breakpoint: {min: 1024, max: 99999},
                      items: 2,
                      slidesToSlide: 1,
                    },
                    tablet: {
                      breakpoint: {max: 1024, min: 780},
                      items: 2,
                      slidesToSlide: 1,
                    },
                    mobile: {
                      breakpoint: {max: 780, min: 0},
                      items: 2,
                      slidesToSlide: 1,
                    },
                  }}
                  controlConfig={{
                    arrowsColor: 'gray800',
                    showDots: false,
                    showArrows: {
                      mobile: showCarouselArrows,
                      tablet: showCarouselArrows,
                      desktop: showCarouselArrows,
                    },
                    disableNextControlCondition: (state) => {
                      return (
                        currentSelectedImageGroupIndex ===
                        (imageGroups?.[0]?.images.length ?? 0) - 1
                      )
                    },
                    disablePrevControlCondition: (state) => {
                      return currentSelectedImageGroupIndex === 0
                    },
                  }}
                  nextCustomHandler={debouncedNext}
                  prevCustomHandler={debouncedPrev}
                >
                  {imageGroups?.[0]?.images?.map((image, idx) => {
                    const src = useProductImage(image)

                    return (
                      <Box
                        key={`${image?.alt}-${idx}`}
                        width="48px"
                        height="48px"
                        borderColor={currentSelectedImageGroupIndex === idx ? 'accent0' : 'gray200'}
                        border="2px"
                        onClick={() => {
                          setCurrentSelectedImageGroupIndex(idx)
                        }}
                      >
                        <img
                          draggable={false}
                          alt={image?.alt}
                          title={image?.title}
                          src={src}
                          loading="eager"
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )
                  })}
                </Carousel>
              </Box>
            )}
          </Box>
          <Stack width={['full']} className={detailsContainer} >
            {/* Only show this stack on desktop */}
            <Stack spacing="20px" display={["none", "flex"]}>
              <Stack spacing="0px">
                <Text data-test-selector="pdp-title" variant="heading2" as="h1">
                  {hit?.name}
                </Text>
                {drainedWeight && (
                  <Text variant="text1" color="textMuted">
                    {drainedWeight}
                  </Text>
                )}
              </Stack>
              {!!productReviewCount && showReviews && (
                <StarRating
                  rating={productRating}
                  reviewCount={productReviewCount}
                  onReviewClick={openReviewTabAndScroll}
                />
              )}
            </Stack>
            <Flex
              direction={['row', 'column']}
              flexGrow="1"
              width="full"
              justifyContent="space-between"
              gap={[null, "52px"]}
              paddingTop={["0px", "8px"]}
            >
              {(nearPromo || bundle) &&
                <Flex direction="column" gap="2px">
                  <NearPromo
                    data-test-selector="product-details-item-promo-badge"
                    promotion={nearPromo}
                    bundle={bundle}
                    hitId={hit.id}
                  />
                  {nearPromo &&
                    <Text variant="text1" color={"gray700"} lineHeight="none">
                      {getPromoExpiryText(nearPromo.startDate, nearPromo.endDate)}
                    </Text>
                  }
                </Flex>
              }
              <Flex
                direction='column'
                flexGrow="1"
                width="full"
                justifyContent="space-between"
              >
                <Stack alignItems='flex-end' paddingBottom='4px' width='full'>
                  <Price
                    price={displayPrice}
                    promotionPrice={promotionPrice}
                    priceInfo={priceInfo}
                    isPDPPage={true}
                  />
                  {pdpBadgeAsset && <Flex justifyContent="center" paddingTop="16px">
                    <PDPBadgeAsset badgeKey={pdpBadgeAsset} />
                  </Flex>}
                </Stack>
                <Flex
                  flexDirection="column"
                  marginTop="10px"
                  gap="10px"
                  alignItems="center"
                  alignSelf="flex-end"
                >
                  {/* Nearness Message */}
                  {nearPromo && <MultiBuyPromoOfferMessage promotion={nearPromo} productId={id} />}
                  {isPurchaseDisabled ? (
                  <>
                    {!disabledProductBecauseOfRestriction ? (
                      <ViewAlternativesButton product={hit} displayPrice={displayPrice} />
                    ) : null}
                  </>
                ) : (
                <UpdateCartItemQuantity
                  product={hit as ProductItem}
                  quantity={clickedQuantity || 0}
                  setQuantity={setClickedQuantity}
                  maxQuantity={restrictedProductInfo?.maxQuantityAllowed}
                  cartLocation={CART_LOCATION.PDP}
                  disabled={!displayPrice || displayPrice <= 0}
                  hitItemIndex={hitItemIndex}
                  renderButton={
                    !restrictedProductInfo?.cannotAddToBasket ? (
                    <ATBButtonPopover
                      hit={hit}
                      isPLPTile={false}
                      isPDPPage={true}
                      ATBEventDetails={{
                          locationOfCart: CART_LOCATION.PDP,
                          viewingCategoryId: '',
                          searchQuery: '',
                        }}
                      onButtonClick={(e) => {
                        addToBasket(e)
                        sendAddToCartData(
                          hit,
                          CART_LOCATION.PDP,
                          '', // No viewingCategoryId on PDP
                          '', // No searchQuery on PDP
                          hitItemIndex
                        )
                      }}
                      displayPrice={displayPrice}
                    />
                    ) : undefined
                  }
                />)}
                </Flex>
              </Flex>
            </Flex>
            {product.productClasses?.includes('MED') &&
              <Flex align='center' style={{ backgroundColor: '#FABB0540', display: 'flex' }} paddingTop='8px' paddingRight='16px' paddingBottom='8px' flexWrap={['wrap', 'nowrap']}>
                <InfoIcon boxSize="20px" marginRight='8px' marginLeft='4px'></InfoIcon>
                <Text>The purchase of medicines is restricted to 2 per order</Text>
              </Flex>
            }
          </Stack>
          <AddToFavouritesButton
            data-test-selector="pdp-favourite-button"
            position="absolute"
            style={{right: 16, top: 16}}
            productid={id}
            autocompletequeryid={productDetail?.product?.__queryID}
          />
        </Flex>
        {disabledProductBecauseOfRestriction && (
          <LearnMoreAlcoholRestrictionModal {...learnMoreAlcRestrictionModal} />
        )}
      </Container>
    )
  }
)